// PredictBadge
// =============
//
// Wave 14ca-fix30: zeigt für eine Tour (Distanz + Höhenmeter) die erwartete
// Akkuverbrauchs-Vorhersage in Prozent. Wird auf TrailDetail / RunDetail /
// CustomTrailDetail-Pages eingefügt.
//
// Logik:
//   1. /api/battery/me holen → wir nehmen die motor/mode-Combo mit dem
//      höchsten ride_count als "Standard".
//   2. /api/battery/predict mit den Tour-Werten + dieser Combo aufrufen.
//   3. Render: kleines Pill mit ⚡ + "~32% battery" + confidence-indikator.
//
// Wenn der User noch nie eine Tour hochgeladen hat → grauer Badge mit
// Link zu Battery-Lab ("Train Battery Lab to see predictions").
//
// Wir cachen das `me` für 60s in-memory damit ein Trail-Detail-Open keinen
// extra Roundtrip kostet wenn der User durch eine Trail-Liste blättert.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getBatteryMe,
  predictBattery,
  type BatteryCalibrationRow,
  type BatteryMeResponse,
  type BatteryPredictResponse,
} from '../lib/batteryApi';

// Wave 14ca-fix35: Cache cached jetzt das ganze BatteryMeResponse (nicht nur
// calibrations) damit wir auch latestBatteryCapacityWh ohne extra Roundtrip
// zur Verfuegung haben.
let cachedMe: { data: BatteryMeResponse; ts: number } | null = null;
const ME_TTL_MS = 60_000;

async function getCachedMe(): Promise<BatteryMeResponse | null> {
  const now = Date.now();
  if (cachedMe && now - cachedMe.ts < ME_TTL_MS) return cachedMe.data;
  try {
    const me = await getBatteryMe();
    cachedMe = { data: me, ts: now };
    return me;
  } catch (err) {
    // Wave 14ca-fix61: bei 401 propagieren wir den Fehler nach oben damit
    // PredictBadge ihn als auth-error visualisieren kann statt als no-data.
    const status = (err as { status?: number })?.status;
    if (status === 401) throw err;
    return null;
  }
}

/** Forciert ein erneutes Holen — z.B. nach einem Battery-Lab-Upload.
    Aktuell von der Battery-Lab-Page nicht aufgerufen, aber als Hook für
    später bereit. */
export function invalidatePredictCache(): void {
  cachedMe = null;
}

export interface PredictBadgeProps {
  /** Tour-Distanz in Kilometern. */
  distanceKm: number;
  /** Tour-Höhenmeter aufwärts in Metern. */
  elevationGainM: number;
  /** Optional Akku-Kapazität (Wh) als expliziter Override. Wenn nicht
      gesetzt, nutzen wir latestBatteryCapacityWh aus /api/battery/me
      (juengster Ride mit eingegebener Kapazitaet), und falls auch das
      null ist als letzten Fallback 750 Wh. */
  batteryCapacityWh?: number;
  /** Wenn true, das Badge bekommt eine Link-Wrapper zu /navi/battery-lab.
      Default true. */
  linkable?: boolean;
}

export function PredictBadge({
  distanceKm,
  elevationGainM,
  batteryCapacityWh,
  linkable = true,
}: PredictBadgeProps) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'no-data' }
    | { kind: 'predicted'; data: BatteryPredictResponse; motorLabel: string }
    | { kind: 'error' }
    | { kind: 'auth-error' }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let me: BatteryMeResponse | null;
      try {
        me = await getCachedMe();
      } catch (err) {
        // Wave 14ca-fix61: getCachedMe propagiert 401 jetzt — wir handeln
        // das hier als auth-error damit der User Bescheid weiss.
        if (cancelled) return;
        const status = (err as { status?: number })?.status;
        if (status === 401) {
          try {
            const { clearStoredToken } = await import('../lib/auth');
            clearStoredToken();
          } catch {
            /* noop */
          }
          setState({ kind: 'auth-error' });
        } else {
          setState({ kind: 'error' });
        }
        return;
      }
      if (cancelled) return;
      if (!me || me.calibrations.length === 0) {
        setState({ kind: 'no-data' });
        return;
      }
      const calibs = me.calibrations;
      // Combo mit höchstem ride_count = "User's Standard".
      // Bei Gleichstand: jüngster updated_at gewinnt.
      const best = [...calibs].sort((a, b) => {
        if (b.ride_count !== a.ride_count) return b.ride_count - a.ride_count;
        return b.updated_at.localeCompare(a.updated_at);
      })[0]!;
      // Wave 14ca-fix35: explizite prop > juengster eingegebener Wert > 750Wh.
      // Vorher wurde immer 750Wh genommen wenn keine Prop gesetzt war, was
      // Map-Page-Predictions um den Faktor capacity/750 verfaelscht hat
      // (User mit 960Wh-Akku sah 28% zu hohe %-Anzeige).
      const effectiveCapacityWh =
        batteryCapacityWh !== undefined
          ? batteryCapacityWh
          : (me.latestBatteryCapacityWh ?? 750);
      try {
        const result = await predictBattery({
          distanceKm,
          elevationGainM,
          motorId: best.motor_id,
          mode: best.mode,
          batteryCapacityWh: effectiveCapacityWh,
        });
        if (cancelled) return;
        setState({
          kind: 'predicted',
          data: result,
          motorLabel: `${best.motor_id} · ${best.mode}`,
        });
      } catch (err) {
        if (cancelled) return;
        // Wave 14ca-fix61: 401 spezial behandeln — sichtbares "Re-login"
        // Badge statt silent null. Plus: stale localStorage-Token clearen
        // damit das nicht weiter mitgesendet und die Cookie-Auth blockiert.
        const status = (err as { status?: number })?.status;
        if (status === 401) {
          try {
            const { clearStoredToken } = await import('../lib/auth');
            clearStoredToken();
          } catch {
            /* noop */
          }
          setState({ kind: 'auth-error' });
        } else {
          setState({ kind: 'error' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [distanceKm, elevationGainM, batteryCapacityWh]);

  if (state.kind === 'loading') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-turbo-700/40 text-white/40 text-xs">
        <BoltIcon /> …
      </span>
    );
  }

  if (state.kind === 'no-data') {
    const inner = (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-turbo-700/40 border border-sky-500/30 text-sky-300 text-xs">
        <BoltIcon /> Train Battery Lab
      </span>
    );
    return linkable ? (
      <Link to="/navi/battery-lab" className="no-underline">
        {inner}
      </Link>
    ) : (
      inner
    );
  }

  if (state.kind === 'auth-error') {
    // Wave 14ca-fix61: sichtbarer Hinweis statt silent null. Klick fuehrt
    // zu Profile damit der User sich neu einloggen kann ohne die App zu
    // deinstallieren. iOS-PWA-ITP-Workaround.
    const inner = (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-turbo-700/40 border border-amber-500/40 text-amber-300 text-xs"
        title="Session expired — tap to re-login"
      >
        <BoltIcon /> Re-login
      </span>
    );
    return linkable ? (
      <Link to="/profile" className="no-underline">
        {inner}
      </Link>
    ) : (
      inner
    );
  }

  if (state.kind === 'error') {
    return null; // silent — wir wollen die UI nicht mit Fehlern zumüllen
  }

  const { data } = state;
  const pct = data.expectedPct ?? 0;
  const conservative = data.conservativePct ?? 0;
  const confidenceColor =
    data.confidence === 'high'
      ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
      : data.confidence === 'medium'
        ? 'text-amber-300 border-amber-500/40 bg-amber-500/10'
        : 'text-white/55 border-turbo-500/30 bg-turbo-700/40';

  const inner = (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border ${confidenceColor}`}
      title={`Predicted ${pct.toFixed(0)}% battery (conservative ${conservative.toFixed(
        0,
      )}%) — ${data.rideCount} ride${data.rideCount === 1 ? '' : 's'} of training data, ${state.motorLabel}`}
    >
      <BoltIcon /> ~{pct.toFixed(0)}% battery
    </span>
  );

  return linkable ? (
    <Link to="/navi/battery-lab" className="no-underline">
      {inner}
    </Link>
  ) : (
    inner
  );
}

function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true">
      <path d="M13 2L3 14h7l-1 8 11-13h-7l1-7z" />
    </svg>
  );
}
